'use client';

import { useState, ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Loader2, AlertTriangle } from 'lucide-react';

export interface DeleteConfirmationDialogProps {
  title: string;
  description: string;
  itemName: string;
  /** Feeds `DialogTrigger asChild`, whose Slot throws on anything but a single element, so this is `ReactElement` rather than `ReactNode` (VALE-1814; see `__tests__/radix-aschild-slot-registry.test.ts`). */
  trigger?: ReactElement;
  onConfirm: () => Promise<void>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  destructiveWarning?: string;
}

export function DeleteConfirmationDialog({
  title,
  description,
  itemName,
  trigger,
  onConfirm,
  open,
  onOpenChange,
  destructiveWarning = 'This action cannot be undone.'
}: DeleteConfirmationDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isControlled = open !== undefined && onOpenChange !== undefined;
  const dialogOpen = isControlled ? open : isOpen;
  const setDialogOpen = isControlled ? onOpenChange : setIsOpen;

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      await onConfirm();
      setDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-destructive h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description.replace('{itemName}', itemName)}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-destructive/10 border-destructive/20 mt-3 rounded-md border p-3">
          <div className="text-destructive flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">{destructiveWarning}</span>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border-destructive/20 rounded-md border p-3">
            <div className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
